import { Component, computed, inject, input, output } from '@angular/core';

import type {
  AggregatedEvaluationsReviewerEvaluation,
  QuestionGroupedForm,
  ReviewerGroupedForm,
} from '@core/evaluations-types';
import { AggregatedEvaluationsGroupBy } from '@core/evaluations-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { Theme } from '@core/theme-util';
import type { SegmentedButtonType } from '@core/user-interface-legacy';
import {
  BadgeComponent,
  CollapsibleCardComponent,
  HeadingComponent,
  IconComponent,
  SegmentedControlComponent,
} from '@core/user-interface-legacy';

import { CandidateOfferEvaluationQuestionComponent } from '../candidate-offer-evaluation-question/candidate-offer-evaluation-question.component';
import { CandidateOfferEvaluationStageComponent } from '../candidate-offer-evaluation-stage/candidate-offer-evaluation-stage.component';

@Component({
  selector: 'rt-candidate-offer-evaluation-form',
  templateUrl: './candidate-offer-evaluation-form.component.html',
  styleUrl: './candidate-offer-evaluation-form.component.less',
  imports: [
    BadgeComponent,
    CandidateOfferEvaluationQuestionComponent,
    CandidateOfferEvaluationStageComponent,
    CollapsibleCardComponent,
    HeadingComponent,
    IconComponent,
    SegmentedControlComponent,
    TranslatePipe,
  ],
})
export class CandidateOfferEvaluationFormComponent {
  private readonly i18n = inject(I18nService);

  protected readonly groupByButtons = computed<
    Array<SegmentedButtonType<AggregatedEvaluationsGroupBy>>
  >(() => [
    {
      value: AggregatedEvaluationsGroupBy.Question,
      label: this.i18n.translate('app.evaluation_tab.form_details.by_question'),
      isActive: this.groupBy() === AggregatedEvaluationsGroupBy.Question,
      isAvailable: true,
    },
    {
      value: AggregatedEvaluationsGroupBy.Reviewer,
      label: this.i18n.translate('app.evaluation_tab.form_details.by_team_member'),
      isActive: this.groupBy() === AggregatedEvaluationsGroupBy.Reviewer,
      isAvailable: true,
    },
  ]);

  protected readonly questions = computed(() => {
    const form = this.form();
    return 'questions' in form ? form.questions : [];
  });

  protected readonly stages = computed(() => {
    const form = this.form();
    return 'stages' in form ? form.stages : [];
  });

  protected readonly hasAnswers = computed(() => this.form().evaluationsCount > 0);

  protected readonly formName = computed(
    () =>
      this.form().interviewTemplateName ||
      this.i18n.translate('app.evaluation_tab.form_details.quick_evaluation'),
  );

  public form = input.required<QuestionGroupedForm | ReviewerGroupedForm>();

  public groupBy = input.required<AggregatedEvaluationsGroupBy>();

  public theme = input<Theme | null>(null);

  public useProsemirror = input(false);

  public showPositionButton = input(true);

  public groupByChanged = output<AggregatedEvaluationsGroupBy>();

  public viewPosition = output();

  public edit = output<AggregatedEvaluationsReviewerEvaluation>();

  public delete = output<AggregatedEvaluationsReviewerEvaluation>();

  protected onGroupByClicked(groupBy: AggregatedEvaluationsGroupBy): void {
    if (groupBy !== this.groupBy()) {
      this.groupByChanged.emit(groupBy);
    }
  }
}
